import { useEffect, useState } from "react";
import { SiTiktok } from "@icons-pack/react-simple-icons";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Building2Icon,
  ChevronDownIcon,
  CircleUserIcon,
  LogOutIcon,
  MegaphoneIcon,
  MessagesSquareIcon,
  SearchIcon,
  SendIcon,
  UserIcon,
} from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useScrollLock } from "usehooks-ts";

import type { BlogLocale } from "@/features/blog/locales";
import type { SiteCopy } from "@/landing/lib/site-copy";
import { checkHasSessionHint } from "@/common/gateway-session";
import { getInitials } from "@/common/get-initials";
import { BASE_URL } from "@/common/links";
import { resolveNavAuthPaint } from "@/common/session-first-paint";
import { cn } from "@/common/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { useSessionAuthState, useSessionSignOut } from "@/hooks/useSession";
import { useUser } from "@/hooks/useUser";
import { DigitLogo } from "@/landing/components/DigitLogo";
import { OutboundCtaLink } from "@/landing/components/OutboundCtaLink";
import {
  CLI_ACCOUNT_URL,
  CLI_SIGNIN_URL,
  CLI_SIGNUP_URL,
  signupCtaLabel,
  useCanonicalAuthUrl,
} from "@/landing/lib/constants";
import {
  hasLocalizedPlatforms,
  useLandingLocale,
  useSiteCopy,
} from "@/landing/lib/site-copy";
import {
  AGENT_PAGES,
  PLATFORM_PAGES,
  USE_CASE_PAGES,
} from "@/landing/lib/site-links";
import { AgentLogo, PlatformMark } from "@/landing/system/marks";

export function CliNav({
  overHero = false,
  variant = "default",
}: {
  overHero?: boolean;
  variant?: "default" | "linkedin" | "wondatwin";
} = {}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // On routes whose hero is a dark full-bleed video (e.g. /wab2), the nav sits
  // transparent over that footage at the top — so render its marks in white
  // there, reverting to the normal frosted dark-text bar once scrolled.
  const onDarkHero = overHero && !scrolled;

  useScrollLock({ autoLock: menuOpen });

  // Frost the nav once the page is scrolled so it stays distinct from the
  // content passing under it; stays fully transparent at the very top.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Anchor links are plain <a href="#...">, not router Links, so they keep the
  // current path — the LinkedIn landing serves at /linkedin on wonda.sh AND at
  // "/" on wondatwin.com, and a router navigation would rewrite the path.
  type NavLink =
    | { label: string; href: string }
    | { label: string; to: "/docs" | "/models" | "/pricing" }
    | { label: string; anchor: string };

  const navLinks: NavLink[] =
    variant === "wondatwin"
      ? [
          { label: "Product", anchor: "why-wonda" },
          { label: "Use Case", anchor: "use-cases" },
          { label: "Docs", href: `${BASE_URL}/docs` },
          { label: "Pricing", anchor: "pricing" },
        ]
      : variant === "linkedin"
        ? [
            { label: "Product", anchor: "why-wonda" },
            { label: "Use Case", anchor: "use-cases" },
            { label: "Docs", to: "/docs" },
            { label: "Pricing", anchor: "pricing" },
          ]
        : [];
  // The default nav is the homepage hero's: full width, logo left, links and
  // auth together on the right (degaus.com's layout). The LinkedIn and Twin
  // pages keep the centred menu.
  const isDefault = variant === "default";

  return (
    <>
      {/* Transparent at the very top so the animated background reads through;
          frosted glass once scrolled so the nav stays distinct from the content
          passing under it. */}
      <header
        className={cn(
          "sticky top-0 z-50 h-[var(--header-height)] w-full border-b transition-all duration-300",
          scrolled && "backdrop-blur-xl",
        )}
        style={{
          borderColor: scrolled ? "var(--cli-border)" : "transparent",
          backgroundColor: scrolled
            ? isDefault
              ? "rgba(227, 230, 229, 0.78)"
              : "rgba(250, 250, 248, 0.7)"
            : "transparent",
          boxShadow: scrolled
            ? "0 10px 30px -16px rgba(0, 0, 0, 0.12)"
            : "none",
        }}
      >
        {/* 3-column grid keeps the menu truly page-centered: the auth cluster's
            width changes across states (sign-in buttons / loading avatar /
            avatar), and flex justify-between would reflow the menu on each. */}
        <nav
          className={cn(
            "mx-auto grid h-full items-center",
            isDefault
              ? "grid-cols-[1fr_auto] px-[clamp(16px,2.9vw,46px)]"
              : "max-w-5xl grid-cols-[1fr_auto_1fr] px-4 sm:px-6",
          )}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2.5 justify-self-start"
            aria-label={
              variant === "wondatwin" ? "Wonda Twin home" : "Wonda home"
            }
          >
            <DigitLogo variant="nav" light={!onDarkHero} />
            {variant === "wondatwin" && (
              <span className="border-l border-[var(--cli-border)] pl-2.5 font-mono text-xs font-medium tracking-[0.12em] text-[var(--cli-text-secondary)] uppercase">
                Twin
              </span>
            )}
          </Link>

          <ul
            className={cn(
              "hidden items-center gap-6 md:flex",
              isDefault && "md:hidden",
            )}
          >
            {navLinks.map((link) => {
              const linkClass = cn(
                "text-sm transition-colors duration-200",
                onDarkHero
                  ? "text-white/85 hover:text-white"
                  : "text-(--cli-text-secondary) hover:text-(--cli-text)",
              );
              return (
                <li key={link.label}>
                  {"anchor" in link ? (
                    <a href={`#${link.anchor}`} className={linkClass}>
                      {link.label}
                    </a>
                  ) : "href" in link ? (
                    <a href={link.href} className={linkClass}>
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.to} className={linkClass}>
                      {link.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          <div
            className={cn(
              "flex items-center justify-self-end",
              isDefault ? "gap-[22px]" : "gap-3",
            )}
          >
            {isDefault && <DefaultNavLinks onDarkHero={onDarkHero} />}
            {variant === "wondatwin" ? (
              <WondatwinAuthButtons onDarkHero={onDarkHero} />
            ) : (
              <NavAuthGate onDarkHero={onDarkHero} />
            )}

            <button
              className="flex h-8 w-8 flex-col justify-center gap-1.5 md:hidden"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span
                className={cn(
                  "block h-[0.125rem] w-6 transition-all duration-300",
                  menuOpen ? "translate-y-[0.3125rem] rotate-45" : "",
                )}
                style={{
                  backgroundColor: onDarkHero ? "#fff" : "var(--cli-text)",
                }}
              />
              <span
                className={cn(
                  "block h-[0.125rem] w-6 transition-all duration-300",
                  menuOpen ? "-translate-y-[0.1875rem] -rotate-45" : "",
                )}
                style={{
                  backgroundColor: onDarkHero ? "#fff" : "var(--cli-text)",
                }}
              />
            </button>
          </div>
        </nav>
      </header>

      {menuOpen && (
        <div
          className="fixed inset-0 z-40 px-6 pt-20 md:hidden"
          style={{ backgroundColor: "var(--cli-bg)" }}
        >
          <nav className="flex flex-col gap-6 py-8">
            {navLinks.map((link) =>
              "anchor" in link ? (
                <a
                  key={link.label}
                  href={`#${link.anchor}`}
                  className="text-lg font-semibold text-(--cli-text)"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              ) : "href" in link ? (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-lg font-semibold text-(--cli-text)"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-lg font-semibold text-(--cli-text)"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ),
            )}

            {isDefault && (
              <DefaultMobileNavLinks onNavigate={() => setMenuOpen(false)} />
            )}

            {variant === "wondatwin" ? (
              <WondatwinMobileAuthButtons />
            ) : (
              <MobileNavAuthGate onNavigate={() => setMenuOpen(false)} />
            )}
          </nav>
        </div>
      )}
    </>
  );
}

type FeatureMenuItem = {
  label: string;
  slug: string;
  kind: "platform" | "feature";
  icon: React.ReactNode;
};

// The pages themselves are listed once in site-links.ts (the footer reads the same lists).
const USE_CASE_ICONS: Record<
  (typeof USE_CASE_PAGES)[number]["copyKey"],
  typeof UserIcon
> = {
  searchProfiles: SearchIcon,
  sendMessages: SendIcon,
  analyzePosts: MessagesSquareIcon,
  marketingAgents: MegaphoneIcon,
  multiAccount: UserIcon,
  competitorAnalysis: Building2Icon,
};

function featureMenu(
  copy: SiteCopy,
): { title: string; items: FeatureMenuItem[] }[] {
  return [
    {
      title: copy.platforms,
      items: PLATFORM_PAGES.map((page) => ({
        ...page,
        kind: "platform",
        icon: <PlatformMark platform={page.slug} />,
      })),
    },
    {
      title: copy.agents,
      items: AGENT_PAGES.map((page) => ({
        ...page,
        kind: "feature",
        icon: <AgentLogo name={page.label} className="h-4 max-w-4" />,
      })),
    },
    {
      title: copy.useCases,
      items: USE_CASE_PAGES.map((page) => {
        const Icon = USE_CASE_ICONS[page.copyKey];
        return {
          ...page,
          label: copy[page.copyKey],
          kind: "feature",
          icon: <Icon className="size-4" />,
        };
      }),
    },
  ];
}

function FeatureMenuLink({
  item,
  className,
  onClick,
}: {
  item: FeatureMenuItem;
  className: string;
  onClick?: () => void;
}) {
  const locale = useLandingLocale();
  const content = (
    <>
      <span
        aria-hidden
        className="flex size-4 flex-none items-center justify-center"
      >
        {item.icon}
      </span>
      {item.label}
    </>
  );
  if (
    item.kind === "platform" &&
    locale !== "en" &&
    hasLocalizedPlatforms(locale)
  ) {
    return (
      <Link
        to="/$locale/platform/$slug"
        params={{ locale, slug: item.slug }}
        className={className}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }
  return item.kind === "platform" ? (
    <Link
      to="/platform/$slug"
      params={{ slug: item.slug }}
      className={className}
      onClick={onClick}
    >
      {content}
    </Link>
  ) : (
    <Link
      to="/feature/$slug"
      params={{ slug: item.slug }}
      className={className}
      onClick={onClick}
    >
      {content}
    </Link>
  );
}

/**
 * Homepage nav links: Features (a panel that opens on hover, and on keyboard
 * focus or a tap via focus-within), Blog and Docs.
 */
function DefaultNavLinks({ onDarkHero }: { onDarkHero: boolean }) {
  const copy = useSiteCopy();
  const locale = useLandingLocale();
  const linkClass = cn(
    "text-[15px] transition-colors duration-200",
    onDarkHero
      ? "text-white/85 hover:text-white"
      : "text-(--cli-text-secondary) hover:text-(--cli-text)",
  );
  // A link click leaves focus inside the panel, which would hold it open.
  const releaseFocus = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <div className="hidden items-center gap-[22px] md:flex">
      <div className="group/features relative">
        <button
          type="button"
          aria-haspopup="true"
          className={cn(
            "inline-flex cursor-pointer items-center gap-1",
            linkClass,
          )}
        >
          {copy.features}
          <ChevronDownIcon className="size-3.5 transition-transform duration-200 group-focus-within/features:rotate-180 group-hover/features:rotate-180" />
        </button>
        <div className="invisible absolute top-full right-[-12px] translate-y-1 pt-3 opacity-0 transition-all duration-200 group-focus-within/features:visible group-focus-within/features:translate-y-0 group-focus-within/features:opacity-100 group-hover/features:visible group-hover/features:translate-y-0 group-hover/features:opacity-100">
          <div className="grid w-max grid-cols-[repeat(3,max-content)] gap-x-10 rounded-2xl bg-[rgb(240_242_241/94%)] p-5 shadow-[inset_0_1px_0_rgb(255_255_255/85%),inset_0_0_0_1px_rgb(22_25_28/9%),0_18px_40px_-18px_rgb(0_0_0/35%)] backdrop-blur-xl">
            {featureMenu(copy).map((section) => (
              <div
                key={section.title}
                className="flex min-w-[140px] flex-col gap-3"
              >
                <p className="text-xs text-[rgb(22_25_28/45%)]">
                  {section.title}
                </p>
                <ul className="flex flex-col gap-2.5">
                  {section.items.map((item) => (
                    <li key={item.slug}>
                      <FeatureMenuLink
                        item={item}
                        onClick={releaseFocus}
                        className="flex items-center gap-2.5 text-sm whitespace-nowrap text-[#16191C] transition-opacity hover:opacity-70"
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      <BlogLink locale={locale} className={linkClass}>
        {copy.blog}
      </BlogLink>
      <Link to="/docs" className={linkClass}>
        {copy.docs}
      </Link>
    </div>
  );
}

function DefaultMobileNavLinks({ onNavigate }: { onNavigate: () => void }) {
  const copy = useSiteCopy();
  const locale = useLandingLocale();
  return (
    <>
      <div className="flex flex-col gap-4">
        <p className="text-lg font-semibold text-(--cli-text)">
          {copy.features}
        </p>
        {featureMenu(copy).map((section) => (
          <div key={section.title} className="flex flex-col gap-2.5 pl-1">
            <p className="text-xs text-(--cli-text-muted)">{section.title}</p>
            {section.items.map((item) => (
              <FeatureMenuLink
                key={item.slug}
                item={item}
                onClick={onNavigate}
                className="flex items-center gap-2.5 text-base text-(--cli-text-secondary)"
              />
            ))}
          </div>
        ))}
      </div>
      <BlogLink
        locale={locale}
        className="text-lg font-semibold text-(--cli-text)"
        onClick={onNavigate}
      >
        {copy.blog}
      </BlogLink>
      <Link
        to="/docs"
        className="text-lg font-semibold text-(--cli-text)"
        onClick={onNavigate}
      >
        {copy.docs}
      </Link>
    </>
  );
}

/** The blog index in the page's language (every landing locale has one). */
function BlogLink({
  locale,
  className,
  onClick,
  children,
}: {
  locale: BlogLocale;
  className: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const search = { page: 1, category: undefined, tag: undefined };
  return locale === "en" ? (
    <Link to="/blog" search={search} className={className} onClick={onClick}>
      {children}
    </Link>
  ) : (
    <Link
      to="/$locale/blog"
      params={{ locale }}
      search={search}
      className={className}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function WondatwinAuthButtons({
  onDarkHero = false,
}: {
  onDarkHero?: boolean;
}) {
  return (
    <div className="hidden items-center gap-3 md:flex">
      <a
        href={`${BASE_URL}/signin`}
        className={cn(
          "text-sm transition-colors duration-200",
          onDarkHero
            ? "text-white/85 hover:text-white"
            : "text-(--cli-text-secondary) hover:text-(--cli-text)",
        )}
      >
        Log in
      </a>
      <Button asChild variant="steward" size="sm">
        <OutboundCtaLink placement="nav">
          {signupCtaLabel("Try now")}
        </OutboundCtaLink>
      </Button>
    </div>
  );
}

function WondatwinMobileAuthButtons() {
  return (
    <div
      className="flex flex-col gap-4 border-t pt-6"
      style={{ borderColor: "var(--cli-border)" }}
    >
      <a
        href={`${BASE_URL}/signin`}
        className="text-lg font-semibold text-(--cli-text-secondary)"
      >
        Log in
      </a>
      <Button asChild variant="steward" size="md" className="w-full">
        <OutboundCtaLink placement="nav">
          {signupCtaLabel("Try Wonda Twin")}
        </OutboundCtaLink>
      </Button>
    </div>
  );
}

/**
 * The server's cookie-derived "a session probably exists here" bit, resolved
 * in the root loader so it is already in the first painted frame. Read from
 * router state rather than the root route module to keep this component out of
 * an import cycle with __root.
 */
function useFirstPaintSessionHint(): boolean {
  return useRouterState({
    select: (state) =>
      (state.matches[0]?.loaderData as { hasSessionHint?: boolean } | undefined)
        ?.hasSessionHint === true,
  });
}

/** SSR-safe auth gate — the session hooks need the query client, which
 *  ClientProviders does not mount during SSR, so the signed-in branch waits
 *  for hydration. The server's cookie-derived hint decides what paints until
 *  then, so a returning user never sees the sign-in buttons flash past. */
function NavAuthGate({ onDarkHero = false }: { onDarkHero?: boolean }) {
  const hasSsrHint = useFirstPaintSessionHint();
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  if (!isClient) {
    // Same input the server rendered from, so hydration matches exactly.
    return resolveNavAuthPaint({
      isClient: false,
      isSignedIn: false,
      isLoaded: false,
      hasHint: hasSsrHint,
    }) === "skeleton" ? (
      <NavAvatarSkeleton />
    ) : (
      <SignedOutButtons onDarkHero={onDarkHero} />
    );
  }
  return <NavAuthGateClient onDarkHero={onDarkHero} hasSsrHint={hasSsrHint} />;
}

function NavAuthGateClient({
  onDarkHero = false,
  hasSsrHint,
}: {
  onDarkHero?: boolean;
  hasSsrHint: boolean;
}) {
  const { isSignedIn, isLoaded } = useSessionAuthState();
  // Gateway resolves the session over the network. While it does, paint the
  // likely state instead of collapsing the cluster: returning users (server
  // hint, or the local one when the server withheld it) get the loading
  // avatar, everyone else keeps the sign-in buttons. A wrong guess
  // self-corrects once presence settles. Legacy reports isLoaded=true so the
  // skeleton branch never triggers there (behavior unchanged).
  const paint = resolveNavAuthPaint({
    isClient: true,
    isSignedIn,
    isLoaded,
    hasHint: hasSsrHint || checkHasSessionHint(),
  });
  if (paint === "dropdown") return <NavUserDropdown onDarkHero={onDarkHero} />;
  if (paint === "skeleton") return <NavAvatarSkeleton />;
  return <SignedOutButtons onDarkHero={onDarkHero} />;
}

function NavAvatarSkeleton() {
  return (
    <div
      className="size-7 flex-shrink-0 animate-pulse rounded-full bg-[var(--cli-bg-hover)]"
      aria-hidden
    />
  );
}

function SignedOutButtons({ onDarkHero = false }: { onDarkHero?: boolean }) {
  const copy = useSiteCopy();
  const signinUrl = useCanonicalAuthUrl(CLI_SIGNIN_URL);
  const signupUrl = useCanonicalAuthUrl(CLI_SIGNUP_URL);

  return (
    <div className="hidden items-center gap-[22px] md:flex">
      <a
        href={signinUrl}
        className={cn(
          "text-[15px] transition-colors duration-200",
          onDarkHero
            ? "text-white/85 hover:text-white"
            : "text-(--cli-text-secondary) hover:text-(--cli-text)",
        )}
      >
        {copy.signIn}
      </a>
      <Button
        asChild
        variant="steward"
        size="sm"
        // Over the dark video hero the Steward ink button vanishes, so invert it
        // to the same white-on-ink CTA used in the hero below.
        className={cn(
          "h-[38px] rounded-full px-5 text-[15px] tracking-normal normal-case",
          onDarkHero && "bg-white text-[var(--ink)] hover:bg-white",
        )}
      >
        <a href={signupUrl}>{copy.join}</a>
      </Button>
    </div>
  );
}

function MobileNavAuthGate({ onNavigate }: { onNavigate: () => void }) {
  const hasSsrHint = useFirstPaintSessionHint();
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  if (!isClient) {
    // Withhold rather than flash: the mobile sheet has no avatar slot to hold,
    // so a returning user gets the space back once the session resolves.
    return resolveNavAuthPaint({
      isClient: false,
      isSignedIn: false,
      isLoaded: false,
      hasHint: hasSsrHint,
    }) === "skeleton" ? null : (
      <MobileSignedOutButtons />
    );
  }
  return (
    <MobileNavAuthGateClient onNavigate={onNavigate} hasSsrHint={hasSsrHint} />
  );
}

function MobileNavAuthGateClient({
  onNavigate,
  hasSsrHint,
}: {
  onNavigate: () => void;
  hasSsrHint: boolean;
}) {
  const { isSignedIn, isLoaded } = useSessionAuthState();
  // See NavAuthGateClient: paint the likely state while the gateway session
  // resolves; legacy is always loaded so this never triggers there.
  const paint = resolveNavAuthPaint({
    isClient: true,
    isSignedIn,
    isLoaded,
    hasHint: hasSsrHint || checkHasSessionHint(),
  });
  if (paint === "dropdown")
    return <MobileSignedInLinks onNavigate={onNavigate} />;
  if (paint === "skeleton") return null;
  return <MobileSignedOutButtons />;
}

// Signed-in mobile account links: the same destinations the desktop
// NavUserDropdown exposes (Account, the feature-flagged TikTok post link,
// Organizations, and sign out), styled like the signed-out mobile block.
function MobileSignedInLinks({ onNavigate }: { onNavigate: () => void }) {
  const handleSignOut = useSessionSignOut();
  const showTikTokPostLink = !!useFeatureFlagEnabled("tiktokPostLinkEnabled");
  const accountUrl = useCanonicalAuthUrl(CLI_ACCOUNT_URL);

  return (
    <div
      className="flex flex-col gap-4 border-t pt-6"
      style={{ borderColor: "var(--cli-border)" }}
    >
      <a
        href={accountUrl}
        className="text-lg font-semibold"
        style={{ color: "var(--cli-text)" }}
        onClick={onNavigate}
      >
        Account
      </a>
      {showTikTokPostLink && (
        <Link
          to="/publish/tiktok"
          className="text-lg font-semibold"
          style={{ color: "var(--cli-text)" }}
          onClick={onNavigate}
        >
          Post to TikTok
        </Link>
      )}
      <Link
        to="/organizations"
        className="text-lg font-semibold"
        style={{ color: "var(--cli-text)" }}
        onClick={onNavigate}
      >
        Organizations
      </Link>
      <button
        type="button"
        onClick={handleSignOut}
        className="text-left text-lg font-semibold"
        style={{ color: "var(--cli-text-secondary)" }}
      >
        Log out
      </button>
    </div>
  );
}

function MobileSignedOutButtons() {
  const copy = useSiteCopy();
  const signinUrl = useCanonicalAuthUrl(CLI_SIGNIN_URL);
  const signupUrl = useCanonicalAuthUrl(CLI_SIGNUP_URL);

  return (
    <div
      className="flex flex-col gap-4 border-t pt-6"
      style={{ borderColor: "var(--cli-border)" }}
    >
      <a
        href={signinUrl}
        className="text-lg font-semibold"
        style={{ color: "var(--cli-text-secondary)" }}
      >
        {copy.signIn}
      </a>
      <Button
        asChild
        variant="steward"
        size="md"
        className="w-full rounded-full tracking-normal normal-case"
      >
        <a href={signupUrl}>{copy.join}</a>
      </Button>
    </div>
  );
}

export function NavUserDropdown({
  onDarkHero = false,
}: {
  onDarkHero?: boolean;
}) {
  const handleSignOut = useSessionSignOut();
  const { isLoaded, user } = useUser();
  const showTikTokPostLink = !!useFeatureFlagEnabled("tiktokPostLinkEnabled");
  const accountUrl = useCanonicalAuthUrl(CLI_ACCOUNT_URL);

  // On the Steward light nav, a no-photo account (`imageUrl` when
  // `!hasImage`) renders an on-brand ink box instead of the provider-default
  // gradient avatar.
  const useInkFallback = !user?.hasImage;
  // On the ink light fallback show initials (not a generic icon), via the shared
  // helper so they match UserButton/settings exactly.
  const navInitials = user
    ? getInitials(user.fullName, user.primaryEmailAddress?.emailAddress)
    : "";

  // The token says signed-in but /oauth/userinfo may still be loading (user
  // is null until it resolves). Show a loading avatar rather than a name-less
  // dropdown; once identity settles the menu renders below (with a generic
  // avatar if userinfo genuinely failed — degraded but functional).
  if (!user && !isLoaded) {
    return <NavAvatarSkeleton />;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full select-none"
        >
          <span className="sr-only">User dropdown</span>
          <Avatar className="size-7 flex-shrink-0 rounded-full">
            {useInkFallback ? null : (
              <AvatarImage src={user?.imageUrl} alt="" />
            )}
            <AvatarFallback
              className={cn(
                "rounded-full bg-[var(--cli-bg-hover)] text-[var(--cli-text-secondary)]",
                useInkFallback && "bg-[#0b0b0c] text-[#fafaf8]",
                // Over the dark video hero, both Steward fallbacks disappear, so
                // render the avatar white-tinted like the rest of the hero nav.
                onDarkHero && "bg-white/15 text-white",
              )}
            >
              {useInkFallback && navInitials ? (
                navInitials
              ) : (
                <UserIcon className="size-4" />
              )}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-64 rounded-lg"
        align="end"
        sideOffset={4}
      >
        {user && (
          <>
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="size-8 rounded-full">
                  {useInkFallback ? null : (
                    <AvatarImage src={user.imageUrl} alt="" />
                  )}
                  <AvatarFallback
                    className={cn(
                      "rounded-full bg-[var(--cli-bg-hover)] text-[var(--cli-text-secondary)]",
                      useInkFallback && "bg-[#0b0b0c] text-[#fafaf8]",
                    )}
                  >
                    {useInkFallback && navInitials ? (
                      navInitials
                    ) : (
                      <UserIcon className="size-4" />
                    )}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user.fullName}
                  </span>
                  <span className="truncate text-xs text-[var(--cli-text-secondary)]">
                    {user.primaryEmailAddress?.emailAddress}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuGroup>
          {showTikTokPostLink && (
            <DropdownMenuItem asChild>
              <Link to="/publish/tiktok">
                <SiTiktok className="size-4" />
                <span>Post to TikTok</span>
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <a href={accountUrl}>
              <CircleUserIcon />
              <span>Account</span>
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/organizations">
              <Building2Icon />
              <span>Organizations</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={handleSignOut}>
          <LogOutIcon />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
